import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Hourglass } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchPastWinners, type PastWinner } from '@/lib/api';
import QuestTimeline, { type PastQuest } from '@/components/QuestTimeline';

export default function PastQuests() {
  const { t, i18n } = useTranslation();
  const [winners, setWinners] = useState<PastWinner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPastWinners({ limit: 24, lang: i18n.language.slice(0, 2) })
      .then(setWinners)
      .catch(() => setWinners([]))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  const totalRaised = winners.reduce((sum, w) => sum + w.total_paid_cents, 0) / 100;
  const totalVotes = winners.reduce((sum, w) => sum + w.total_votes, 0);
  const totalDelivered = winners.filter((w) => w.video_published_url).length;
  const totalRefunded = winners.filter((w) => w.refunded).length;
  const deliveryRate = winners.length ? Math.round((totalDelivered / winners.length) * 100) : 0;

  const timelineData = useMemo<PastQuest[]>(
    () =>
      winners
        .filter((w) => w.video_published_url || !w.refunded)
        .map((w) => ({
          id: w.quest.id,
          week_iso: w.week_iso,
          title: w.quest.title,
          author: w.quest.author_username,
          total_cents: w.total_paid_cents,
          video_url: w.video_published_url ?? '',
          date: w.decided_at,
        })),
    [winners],
  );

  return (
    <>
      <section className="section pb-12">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <h1 className="font-pixel text-5xl sm:text-6xl font-bold tracking-tight">
              {t('past.title')}
            </h1>
            <p className="mt-4 text-kquest-300 text-lg max-w-2xl mx-auto">
              {t('past.subtitle')}
            </p>
          </div>

          {winners.length > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                <Stat label={t('past.stat_weeks')} value={`${winners.length}`} />
                <Stat label={t('past.stat_raised')} value={`€${totalRaised.toLocaleString('it-IT')}`} accent="gold" />
                <Stat label={t('past.stat_votes')} value={totalVotes.toLocaleString('it-IT')} />
                <Stat label={t('past.stat_delivery')} value={`${deliveryRate}%`} accent="xp" />
              </div>
              {totalRefunded > 0 && (
                <div className="mt-6 text-center text-sm text-kquest-300 font-pixel">
                  <AlertCircle className="inline w-4 h-4 text-redstone-400 mr-1" />
                  {t('past.refunded_note', { count: totalRefunded })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="section pt-0">
        {loading ? (
          <div className="text-center text-kquest-300 font-pixel py-16">Loading…</div>
        ) : timelineData.length === 0 ? (
          <EmptyState />
        ) : (
          <QuestTimeline pastQuests={timelineData} />
        )}
      </section>
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="container-narrow max-w-2xl text-center">
      <div className="kblock p-12">
        <Hourglass className="w-16 h-16 text-violet-400 mx-auto mb-6 opacity-80" />
        <h2 className="font-pixel text-2xl sm:text-3xl mb-4">
          {t('past.empty_title', { defaultValue: 'Nessuna quest realizzata… ancora' })}
        </h2>
        <p className="text-kquest-300 leading-relaxed">
          {t('past.empty_body', {
            defaultValue: 'La prima quest vincitrice apparirà qui appena verrà conclusa la prima settimana di voti. Vai su /quests per votare quella che vuoi vedere realizzata.',
          })}
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: 'gold' | 'xp' }) {
  return (
    <div className="kblock p-5 text-center">
      <div className={`font-press text-xl sm:text-2xl ${
        accent === 'gold' ? 'text-gold-400' :
        accent === 'xp'   ? 'text-xp-400'   :
        'text-kquest-100'
      }`}>
        {value}
      </div>
      <div className="mt-2 text-[10px] sm:text-xs font-pixel uppercase tracking-wider text-kquest-400">
        {label}
      </div>
    </div>
  );
}
